import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UpperFirstPipe } from 'angular-pipes';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { WizardService } from '../../../services/wizard/wizard.service';
import { EventService } from '../../../services/event/event.service';

/**
 * Meeting Title's value may be affected by Wizard DefaultOptions
 */
@Component({
    selector: 'wizard-meeting-title',
    templateUrl: './meeting-title.component.html',
    styleUrls: ['../../form-styles.scss'],
    providers: [UpperFirstPipe]
})
export class MeetingTitleComponent extends BaseComponent implements OnInit {
    public meetingSubject = new FormControl('');
    public isLoading = true;
    private defaultsSubscription: Subscription[] = [];

    constructor(
        public wizardService: WizardService,
        private eventService: EventService,
        public upperfirst: UpperFirstPipe
    ) {
        super();
    }

    ngOnInit() {
        const isPlugin = this.wizardService.config.isPlugin;
        this.setDefaultSubject(isPlugin, this.wizardService.options.subject);
        this.initListeners();
    }

    ngOnDestroy() {
        this.defaultsSubscription.forEach((sub) => sub.unsubscribe);
    }

    private initListeners() {
        const newDefaultOptionsListener = this.wizardService.onNewDefaultOptions$
            .pipe(skip(1))
            .subscribe((defaults) => {
                this.setNewDefaultSubject(defaults.subject);
            });
        const meetingSubjectListener = this.meetingSubject.valueChanges
            .pipe(distinctUntilChanged(), debounceTime(500))
            .subscribe((subject: string) => {
                this.updateWizard(subject);
            });

        this.defaultsSubscription.push(newDefaultOptionsListener);
        this.defaultsSubscription.push(meetingSubjectListener);
    }

    private setDefaultSubject(isPlugin: boolean, subject?: string) {
        /**
         * 1. If a custom subject exists, use it
         * 2. If a previous default subject exists, use it
         * 3. If there is no subject (e.g. set to null by enableSubjectUpdate in WizardService), fetch the default subject from server
         */
        if (this.wizardService.isCustomSubject && this.wizardService.customSubject) {
            this.updateSubject(this.wizardService.customSubject);
        } else if (subject) {
            this.updateSubject(subject);
        } else if (isPlugin) {
            // This is temporary until we get dynamic titles to work in plugin
            const temporaryTitle = `Meet with ${this.wizardService.currentUser.name}`;
            this.updateSubject(temporaryTitle);
        } else {
            this.fetchDefaultSubject();
        }
    }

    private setNewDefaultSubject(subject?: string) {
        if (!subject && this.wizardService.config.isPlugin) {
            subject = `Meet with ${this.wizardService.currentUser.name}`;
        }

        if (!subject || this.wizardService.isCustomSubject) return;

        this.updateSubject(subject);
    }

    updateSubject(val: string) {
        const newVal = val ? this.upperfirst.transform(val) : '';
        this.meetingSubject.setValue(newVal);
        this.updateWizard(newVal);
        this.isLoading = false;
    }

    private updateWizard(value: string) {
        this.wizardService.updateOptions({ subject: value });
        this.wizardService.customSubject = value;
    }

    private fetchDefaultSubject() {
        this.eventService.requestDefaultOptions(['contacts', 'type']).subscribe((response) => {
            const { data } = response;

            this.updateSubject(data.subject);
        });
    }

    handleKeyup() {
        this.wizardService.isCustomSubject = Boolean(this.meetingSubject.value);
    }
}
